/* eslint-disable no-unused-vars */
import { store } from 'redux/store';
import _ from 'lodash';

import siteAutoPopulate from './site-auto-populate';
import equipmentAutoPopulate from './equipment-auto-populate';
import relatedPsvAndPvAutoPopulate from './related-psv-pv-auto-populate';
import structureAutoPopulate from './structure-auto-populate';
import pipelineAutoPopulate from './pipeline-auto-populate';
import multipleSiteAutoPopulate from './multiple-site-auto-populate';
import { decodeToken } from '@utilities/authentication';
import { transformNewAndEditEquipmentDataForAutoPopulate } from 'transformers/equipment';
import { convertMillimeterToInches } from '@utilities/conversion-mm-inch';

export const getSiteAutoPopulate = ({
  siteId = null,
  equipmentId = null,
  pipelineId = null,
  structureId = null,
  psvsList = [],
  inspectionType = null,
  frequencyId = null,
  currentTaskId = null
}) => {
  try {
    return new Promise((resolve) => {
      const {
        picklists,
        cache,
        jurisdictionNumbers,
        structure,
        pvPsv,
        pipeline,
        inspection,
        utFormData
      } = store.getState();
      const { utFormDataOfInspection } = utFormData;
      const { areaHierarchyTreeDataMap = {}, picklistsMap } = picklists;
      const { jurisdictionNumbersMap = {} } = jurisdictionNumbers;
      const { structureMobileMap = {}, structureMobile = [] } = structure;
      const { pvPsvMap = {} } = pvPsv;
      const { pipelineMobileMap = {}, pipelineMobile = [] } = pipeline;
      const {
        inspectionLastDate,
        inspectionTypesMap,
        equipmentFormsUserCacheMap,
        smallMeasurement
      } = inspection;

      const {
        sitesMap = {},
        equipmentsMap = {},
        equipments: cacheEquipments = [],
        sites: cacheSites = []
      } = cache;
      const intervalId = frequencyId || inspectionTypesMap[inspectionType]?.frequencyId || '';

      const site = cache?.sites?.find((site) => site?.siteId === siteId);
      let equipment = cache?.equipments?.find((equ) => equ?.equipmentId === equipmentId);
      if (equipment?.moreFields) {
        const fieldSourcePropertyWIthValue = transformNewAndEditEquipmentDataForAutoPopulate({
          asset: equipment,
          equipmentFormsUserCacheMap,
          jurisdictionNumbersMap
        });
        if (fieldSourcePropertyWIthValue) {
          equipment = {
            ...equipment,
            ...fieldSourcePropertyWIthValue
          };
        }
      }

      const checkSheetStructureData =
        (structureId && structureMobile[structureMobileMap[structureId]]) || {};

      const { jurisdictionNumber, jurisdiction } =
        jurisdictionNumbersMap[equipment?.equipmentId] || {};
      const structureData = structureMobile[structureMobileMap[equipment?.structureId]] || {};
      const { pv = [], psv = [] } = pvPsvMap[equipmentId] || {};
      const psvs = psv
        .filter((id) => cacheEquipments[equipmentsMap[id]])
        ?.map((x) => cacheEquipments[equipmentsMap[x]]);

      const pvs = pv
        .filter((id) => cacheEquipments[equipmentsMap[id]])
        ?.map((x) => cacheEquipments[equipmentsMap[x]]);

      const pipeLineData = pipelineMobile[pipelineMobileMap[pipelineId]] || {};
      const pipelineSiteIds = [
        ...(pipeLineData?.sites || []),
        ...(pipeLineData?.wells || []),
        pipeLineData?.fromSite
      ];

      const relatedPVId = pvs[0]?.equipmentId ? pvs[0]?.equipmentId : null;
      const relatedPSVId = psvs[0]?.equipmentId ? psvs[0]?.equipmentId : null;

      const utData = utFormDataOfInspection[currentTaskId] || [];

      let photos = [];
      let documents = [];
      let cml = [];
      let imagesFailed = false;
      const inchUnit = smallMeasurement?.filter((x) => x?.lookupValue === 'in')?.[0]?.value || '';
      const mmUnit = smallMeasurement?.filter((x) => x?.lookupValue === 'mm')?.[0]?.value || '';
      for (let i of utData) {
        imagesFailed = i?.imagesFailed || false;
        const cmlFormDataList = i?.readings || [];
        const utPhotos = i?.photos?.map((x) => {
          return {
            category: x?.category || null,
            caption: x?.caption || null,
            imageURL: x.url || null,
            photoId: x?.photoId || null
          };
        });
        photos.push(...utPhotos);

        const utDocuments = i?.documents?.map((x) => {
          return {
            category: x?.category || null,
            files: x?.url || null,
            fileName: x?.fileName || null,
            documentId: x?.documentId || null
          };
        });
        documents.push(...utDocuments);

        for (let cmlFormData of cmlFormDataList) {
          const dataPoints = [
            {
              Id: 'Component',
              key: 'component',
              value: picklistsMap[cmlFormData?.component]?.value || '',
              title: 'Component'
            },
            {
              Id: 'Location',
              key: 'location',
              value: picklistsMap[cmlFormData?.location]?.value || '',
              title: 'Location'
            },
            {
              Id: 'Orientation',
              key: 'orientation',
              value: picklistsMap[cmlFormData?.orientation]?.value || '',
              title: 'Orientation'
            },
            {
              Id: 'Material',
              key: 'material',
              value: cmlFormData?.material ? picklistsMap[cmlFormData?.material]?.value || '' : '',
              title: 'Material'
            }
          ];

          let lastAverageReadingInches = convertMillimeterToInches({
            value: cmlFormData?.lastAverageReadingMillimetres || null
          });
          lastAverageReadingInches = lastAverageReadingInches
            ? `${splitDecimalPoint({ boxValue: lastAverageReadingInches, selectedUnit: 'in' })} in`
            : '';

          const lastAverageReadingMillimetres = cmlFormData?.lastAverageReadingMillimetres
            ? `${splitDecimalPoint({
                boxValue: cmlFormData?.lastAverageReadingMillimetres,
                selectedUnit: 'mm'
              })} mm`
            : '';

          let lastMinimumReadingInches = convertMillimeterToInches({
            value: cmlFormData?.lastMinimumReadingMillimetres || null
          });
          lastMinimumReadingInches = lastMinimumReadingInches
            ? `${splitDecimalPoint({
                boxValue: lastMinimumReadingInches,
                selectedUnit: 'in'
              })} in`
            : '';

          const lastMinimumReadingMillimetres = cmlFormData?.lastMinimumReadingMillimetres
            ? `${splitDecimalPoint({
                boxValue: cmlFormData?.lastMinimumReadingMillimetres,
                selectedUnit: 'mm'
              })} mm`
            : '';

          cml.push({
            id: cmlFormData?.id,
            isCached: true,

            cmlNumber: cmlFormData?.cmlNumber,
            component: cmlFormData?.component,
            location: cmlFormData?.location,
            orientation: cmlFormData?.orientation,
            material: cmlFormData?.material,
            schedule: cmlFormData?.schedule,
            outsideDiameter: {
              textBoxValue: convertMillimeterToInches({
                value: cmlFormData?.outsideDiameter || null
              }),
              // textBoxUnitValue: cmlFormData?.outsideDiameter_Unit || ''
              textBoxUnitValue: cmlFormData?.outsideDiameter ? inchUnit : ''
            },
            nominalDiameter: {
              textBoxValue: convertMillimeterToInches({
                value: cmlFormData?.nominalDiameter || null
              }),
              // textBoxUnitValue: cmlFormData?.nominalDiameter_Unit || '',
              textBoxUnitValue: cmlFormData?.nominalDiameter ? inchUnit : ''
            },
            wallThickness: {
              textBoxValue: convertMillimeterToInches({
                value: cmlFormData?.wallThickness || null
              }),
              // textBoxUnitValue: cmlFormData?.wallThickness_Unit || '',
              textBoxUnitValue: cmlFormData?.wallThickness ? inchUnit : ''
            },
            tmin: {
              textBoxValue: convertMillimeterToInches({
                value: cmlFormData?.tmin || null,
                toFixed: 8
                // toFixedValue: 4
              }),
              // textBoxUnitValue: cmlFormData?.tmin_Unit || '',
              textBoxUnitValue: cmlFormData?.tmin ? inchUnit : ''
            },

            outsideDiameterOnMM: {
              textBoxValue: cmlFormData?.outsideDiameter || null,
              textBoxUnitValue: cmlFormData?.outsideDiameter ? mmUnit : ''
            },
            nominalDiameterOnMM: {
              textBoxValue: cmlFormData?.nominalDiameter || null,
              textBoxUnitValue: cmlFormData?.nominalDiameter ? mmUnit : ''
            },
            wallThicknessOnMM: {
              textBoxValue: cmlFormData?.wallThickness || null,
              textBoxUnitValue: cmlFormData?.wallThickness ? mmUnit : ''
            },
            tminOnMM: {
              textBoxValue: cmlFormData?.tmin || null,
              textBoxUnitValue: cmlFormData?.tmin ? mmUnit : ''
            },

            lastAverageReadingInches,
            lastAverageReadingMillimetres,
            lastMinimumReadingInches,
            lastMinimumReadingMillimetres,

            errorExists: '',
            isDone: false,
            isSkipped: false,
            isAddReading: true,
            dataPoints
          });
        }
      }

      const decodesUserData = decodeToken();
      const object = {
        // appliesToType
        Site: {
          ...siteAutoPopulate({
            intervalId,
            firstName: decodesUserData.firstName,
            lastName: decodesUserData.lastName,
            site,
            areaHierarchyTreeDataMap,
            inspectionLastDate: inspectionLastDate?.siteLastInspectionDateWithIds || [],
            userId: decodesUserData?.userId || ''
          })
        },
        // appliesToType
        Equipment: {
          ...equipmentAutoPopulate({
            intervalId,
            firstName: decodesUserData.firstName,
            lastName: decodesUserData.lastName,
            equipment,
            jurisdictionNumber,
            jurisdiction,
            inspectionLastDate: inspectionLastDate?.equipmentLastInspectionDateWithIds || [],
            userId: decodesUserData?.userId || ''
          }),

          ...relatedPsvAndPvAutoPopulate({
            psv: psvs?.length > 0 && psvs[0],
            pv: pvs?.length > 0 && pvs[0],
            jurisdictionNumbersMap
          }),

          ...siteAutoPopulate({
            intervalId,
            site: cacheSites[sitesMap[equipment?.siteId]],
            areaHierarchyTreeDataMap,
            parent: true,
            inspectionLastDate: inspectionLastDate?.siteLastInspectionDateWithIds || [],
            userId: decodesUserData?.userId || ''
          }),
          ...structureAutoPopulate({
            intervalId,
            structure: structureData,
            parent: true
          })
        },
        Structure: {
          ...structureAutoPopulate({
            intervalId,
            firstName: decodesUserData.firstName,
            lastName: decodesUserData.lastName,
            structure: checkSheetStructureData,
            userId: decodesUserData?.userId || ''
          })
        },
        // appliesToType
        Pipeline: {
          // sourceEntity
          ...pipelineAutoPopulate({
            intervalId,
            firstName: decodesUserData.firstName,
            lastName: decodesUserData.lastName,
            pipeline: pipeLineData,
            inspectionLastDate: inspectionLastDate?.pipelineInspectionDateWithIds || [],
            userId: decodesUserData?.userId || ''
          }),

          Site: {
            ...multipleSiteAutoPopulate({
              siteIds: [...pipelineSiteIds],
              cacheSites,
              sitesMap,
              areaHierarchyTreeDataMap,
              inspectionLastDate: inspectionLastDate?.siteLastInspectionDateWithIds || []
            })
          }
        },
        PSVS: {
          data: convertPSVsDataToFormWrapperData({ psvs: psvsList })
        },
        relatedPVId: relatedPVId,
        relatedPSVId: relatedPSVId,
        UT_CML_Section: {
          photos: photos,
          documents: documents,
          cml: cml,
          selectedUnit: 'in',
          imagesFailed
        }
      };
      return resolve(object);
    });
  } catch (e) {
    console.log({ e });
  }
};

const convertPSVsDataToFormWrapperData = ({ psvs = [] }) => {
  return psvs?.map((psv) => {
    return {
      name: psv?.name || '',
      equipmentId: psv?.equipmentId,
      verificationStatus: psv?.verificationStatus || 'verify',
      isDetached: psv?.isDetached || false,
      isReattach: psv?.isReattach || false,
      isNew: psv?.isNew || false,

      category: psv?.category || '',
      type: psv?.type || '',
      subType: psv?.subType || '',

      equipmentCategory: psv?.equipmentCategory,
      equipmentType: psv?.equipmentType,
      equipmentSubType: psv?.equipmentSubType,

      serialNumber: psv?.serialNumber || '',
      equipmentNumber: psv?.equipmentNumber,
      status: psv?.status,
      psvCategoryTypeSubType: psv?.psvCategoryTypeSubType || '',
      valveLocation: psv?.valveLocation || '',
      pressure: {
        textBoxValue: psv?.pressure || null,
        textBoxUnitValue: psv?.pressureMeasurement || ''
      },
      manufacturer: psv?.manufacturer || '',
      model: psv?.model,
      fieldTagNumber: psv?.fieldTagNumber,
      crnNumber: psv?.crnNumber || '',
      psvCapacity: {
        textBoxValue: psv?.psvCapacity || null,
        textBoxUnitValue: psv?.psvCapacityMeasurement || ''
      },
      inletSize: {
        textBoxValue: psv?.inletSize || null,
        textBoxUnitValue: psv?.inletSizeMeasurement || ''
      },
      inletJointStyle: psv?.flangeInletJointStyle || '',
      outletSize: {
        textBoxValue: psv?.outletSize || null,
        textBoxUnitValue: psv?.outletSizeMeasurement || ''
      },
      outletJointStyle: psv?.flangeOutletJointStyle || '',
      lastServiceDate: psv?.lastServiceDate || '',
      comments: psv?.comments || '',
      expand: !psv?.isNew,
      dataPoints: psv?.dataPoints || []
    };
  });
};

const splitDecimalPoint = ({ boxValue, selectedUnit }) => {
  let textBoxValue = Number(boxValue);
  let newValue = textBoxValue.toFixed(selectedUnit === 'in' ? 4 : 5);

  let originalValue = newValue;
  originalValue = originalValue.toString();
  let [value, decimal] = originalValue.split('.');

  let latestValue = newValue;
  if (decimal) {
    let newDecimal = decimal.slice(-1);
    if (newDecimal === '0') {
      let bo = decimal.slice(0, 3);
      latestValue = `${value}.${bo}`;
    } else {
      latestValue = newValue;
    }
  } else {
    latestValue = newValue;
  }
  return latestValue;
};
